import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowsRotate,
  faBan,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faClipboardList,
  faHourglassHalf,
  faPaperPlane,
  faPenToSquare,
  faPlusCircle,
  faRefresh,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";

export const chaseTableActions = [
  {
    name: "Edit Chase",
    icon: faPenToSquare,
  },
  {
    name: "Delete Chase",
    icon: faTrash,
  },
  {
    name: "Restart Chase",
    icon: faRefresh,
  },
];

// kept for backwards compatibility where something still imports faPlusCircle
export const _legacyIcons = { faPlusCircle };

export type TableHeading = {
  header: string;
  accessor: string;
  /** Alignment utility applied to the header cell, e.g. "text-right" */
  align?: string;
  colSpan?: number;
};

export const chaseHeadingData: TableHeading[] = [
  {
    header: "Customer name",
    accessor: "customerName",
  },
  {
    header: "Contact details",
    accessor: "contactDetails",
  },
  {
    header: "Type",
    accessor: "type",
  },
  {
    header: "Insurer",
    accessor: "insurer",
  },
  {
    header: "Dates",
    accessor: "customerExCode",
  },
  {
    header: "Driver",
    accessor: "driver",
  },
  {
    header: "Status",
    accessor: "status",
  },
  {
    header: "Activity",
    accessor: "activity",
  },
  {
    // spans the actions menu and the row-expand toggle
    header: "Actions",
    accessor: "actions",
    align: "text-right",
    colSpan: 2,
  },
];

export type ChaseStatusRow = {
  name: string;
  bgColor: string;
  icon: IconDefinition;
  color: string;
  /** A step darker than the tint — written out in full so Tailwind keeps the classes. */
  borderColor: string;
};

export const chaseStatusData: ChaseStatusRow[] = [
  {
    name: "Awaiting-Customer",
    bgColor: "bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30",
    icon: faHourglassHalf,
    color: "text-amber-700 dark:text-amber-300",
    borderColor: "border border-amber-300 dark:border-amber-500/40",
  },
  {
    name: "Pending-Review",
    bgColor: "bg-indigo-50 dark:bg-indigo-500/15 border border-indigo-200 dark:border-indigo-500/30",
    icon: faClipboardList,
    color: "text-indigo-700 dark:text-indigo-300",
    borderColor: "border border-indigo-300 dark:border-indigo-500/40",
  },
  {
    name: "Approved",
    bgColor: "bg-emerald-50 dark:bg-emerald-500/15 border border-emerald-200 dark:border-emerald-500/30",
    icon: faCircleCheck,
    color: "text-emerald-700 dark:text-emerald-300",
    borderColor: "border border-emerald-300 dark:border-emerald-500/40",
  },
  {
    name: "Rejected",
    bgColor: "bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30",
    icon: faCircleExclamation,
    color: "text-red-700 dark:text-red-300",
    borderColor: "border border-red-300 dark:border-red-500/40",
  },
  {
    name: "Failed",
    bgColor: "bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/30",
    icon: faTriangleExclamation,
    color: "text-amber-700 dark:text-amber-300",
    borderColor: "border border-amber-300 dark:border-amber-500/40",
  },
  {
    name: "Cancelled",
    bgColor: "bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30",
    icon: faCircleXmark,
    color: "text-red-700 dark:text-red-300",
    borderColor: "border border-red-300 dark:border-red-500/40",
  },
  {
    name: "Rebroked",
    bgColor: "bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30",
    icon: faBan,
    color: "text-red-700 dark:text-red-300",
    borderColor: "border border-red-300 dark:border-red-500/40",
  },
  {
    name: "Mismatched",
    bgColor: "bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30",
    icon: faBan,
    color: "text-red-700 dark:text-red-300",
    borderColor: "border border-red-300 dark:border-red-500/40",
  },
];

export const enhancedChaseStatusData: ChaseStatusRow[] = [
  ...chaseStatusData,
  {
    name: "Pending-Submitted",
    bgColor: "bg-fuchsia-50 dark:bg-fuchsia-500/15 border border-fuchsia-200 dark:border-fuchsia-500/30",
    icon: faPaperPlane,
    color: "text-fuchsia-700 dark:text-fuchsia-300",
    borderColor: "border border-fuchsia-300 dark:border-fuchsia-500/40",
  },
  {
    name: "Awaiting-Submitted",
    bgColor: "bg-orange-50 dark:bg-orange-500/15 border border-orange-200 dark:border-orange-500/30",
    icon: faArrowsRotate,
    color: "text-orange-700 dark:text-orange-300",
    borderColor: "border border-orange-300 dark:border-orange-500/40",
  },
];

export const ChaseInputTypeData = [
  {
    id: "nationalInsuranceNumber",
    label: "National Insurance Number",
  },
  {
    id: "driversLicenceNumber",
    label: "Drivers License Number",
  },
  {
    id: "postCode",
    label: "Post Code",
  },
];
